import React from 'react';
import { FiChevronLeft, FiChevronRight, FiFilter } from 'react-icons/fi';
import ReviewerQuestionnairesHeader from './ReviewerQuestionnairesHeader';
import QuestionCard from './QuestionCard';
import { ReviewerProvider, useReviewerContext } from '../../context/ReviewerContext';

// ─── Component ─────────────────────────────────────────────────────────────

const ReviewerQuestionnairesContent = () => {

  // Get everything from context
  const {
    // States
    loading,
    error,
    questionsLoading,
    selectAll,
    currentPage,

    queryFilter,
    setQueryFilter,

    // Computed values
    paginatedQuestions,
    allReviewed,
    anySelected,
    anyPending,
    totalPages,

    // Setters
    setCurrentPage,

    // Functions
    handleUndoAll,
    handleApproveAll,
    handleRejectAll,
    handleApproveSelected,
    handleRejectSelected,
    toggleSelectAll,
    crosswalkLoading,
    proposalStatus,
    proposal,
  } = useReviewerContext();

  const STATUS_CONFIG = {
    completed: { label: 'Completed', bg: '#e6f4ea', color: '#1a7f37', dot: '#1a7f37' },
    no_proposals: { label: 'Completed', bg: '#fff3cd', color: '#856404', dot: '#f59e0b' },
    timeout: { label: 'Failed', bg: '#fff3cd', color: '#856404', dot: '#f59e0b' },
    save_failed: { label: 'Failed', bg: '#fde8e8', color: '#c0392b', dot: '#c0392b' },
    polling: { label: 'Polling…', bg: '#e8f0fe', color: '#1a5fcc', dot: '#1a5fcc' },
    failed: { label: 'Failed', bg: '#fde8e8', color: '#c0392b', dot: '#c0392b' },
  };

  // ── Render ────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className=" p-5 bg-white cardStyle">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <div className="w-48 h-6 bg-[#e6eef5] animate-pulse mb-2" />
              <div className="w-64 h-4 bg-[#e6eef5] animate-pulse" />
            </div>
            <div className="flex items-center gap-3">
              <div className="w-28 h-10 bg-[#e6eef5] animate-pulse" />
              <div className="w-40 h-10 bg-[#e6eef5] animate-pulse" />
            </div>
          </div>

          {/* Scrollable cards skeleton */}
          <div className="flex items-center gap-2">
            <button className="p-1 rounded-full text-[#919191] flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-[#e6eef5] animate-pulse" />
            </button>
            <div className="flex gap-3 overflow-x-auto flex-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="min-w-[170px] max-w-[190px]  p-3 bg-white border border-[#e6eef5]">
                  <div className="w-32 h-4 bg-[#e6eef5] animate-pulse mb-3" />
                  <div className="w-full h-3 bg-[#e6eef5] animate-pulse mb-2" />
                  <div className="w-full h-3 bg-[#e6eef5] animate-pulse" />
                </div>
              ))}
            </div>
            <button className="p-1 rounded-full text-[#919191] flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-[#e6eef5] animate-pulse" />
            </button>
          </div>
        </div>

        {/* Auto-selected answers skeleton */}
        <div className=" p-5 bg-white cardStyle">
          <div className="flex items-center justify-between mb-4">
            <div className="w-48 h-5 bg-[#e6eef5] animate-pulse" />
            <div className="w-32 h-8 bg-[#e6eef5] animate-pulse" />
          </div>



          {/* Questions list skeleton */}
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="relative border-2  p-6">


                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="w-28 h-4 bg-[#e6eef5] animate-pulse mb-2" />
                    <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                  </div>
                  <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                </div>

                {/* Options skeleton */}
                <div className="grid grid-cols-1 gap-3">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div key={j} className="flex items-center gap-4">
                      <div className="w-4 h-4 rounded-full bg-[#e6eef5] animate-pulse" />
                      <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                    </div>
                  ))}
                </div>

                {/* Confidence label skeleton */}
                <div className="mt-4">
                  <div className="w-24 h-3 bg-[#e6eef5] animate-pulse mb-2" />
                  <div className="flex items-center gap-3">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <div key={k} className="w-14 h-3 bg-[#e6eef5] animate-pulse" />
                    ))}
                  </div>
                </div>

                {/* Undo button skeleton bottom-left */}
                <div className="mt-4">
                  <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500 text-sm">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 relative">
      {/* ─── Header: Questionnaires ─── */}
      <ReviewerQuestionnairesHeader />

      {/* ─── Auto-Selected Answers Section ─── */}
      <div className=" p-5 bg-white cardStyle">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-[#0077b5]">
              Auto-Selected Answers
            </h3>
            {crosswalkLoading && (
              <span className="flex items-end gap-[3px] pb-[2px]">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{ animationDelay: `${i * 0.18}s` }}
                    className="block w-[6px] h-[6px] rounded-full bg-[#0077b5] animate-bounce"
                  />
                ))}
              </span>
            )}
          </div>

          {/* ── Top-right: Query filter + Crosswalk info + Undo all ── */}
          <div className="flex items-center gap-3">
            {/* Query filter — filters the question list by chat thread status */}
            <div className="flex items-stretch border border-[#c8dcea] text-xs overflow-hidden">
              <span className="px-3 py-1.5 bg-[#f4f8fb] flex items-center gap-1.5 text-[#53545a] font-semibold border-r border-[#c8dcea] whitespace-nowrap">
                <FiFilter className="text-[#0077b5]" />
                Query
              </span>
              <select
                value={queryFilter}
                onChange={(e) => setQueryFilter(e.target.value)}
                className="px-3 py-1.5 bg-white text-[#53545a] outline-none cursor-pointer appearance-none pr-7"
              >
                <option value="all">All Questions</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
                <option value="any">All Query</option>
              </select>
            </div>

            {/* Crosswalk badge — always in DOM to prevent layout shift */}
            <div className="flex items-stretch overflow-hidden border border-[#c8dcea] text-xs">
              {/* Label segment */}
              <span className="px-3 py-1.5 bg-[#0077b5] text-white font-bold  flex items-center whitespace-nowrap">
                Questionnaire intelligence
              </span>

              {/* Status segment */}
              <span className="w-[110px] px-3 py-1.5 bg-[#f4f8fb] flex items-center gap-1.5 border-l border-[#c8dcea]">
                {crosswalkLoading ? (
                  <div className="w-full h-2.5 bg-[#c8dcea] animate-pulse" />
                ) : (() => {
                  const cfg = proposalStatus
                    ? (STATUS_CONFIG[proposalStatus] || { label: proposalStatus, color: '#53545a', dot: '#919191' })
                    : null;
                  return cfg ? (
                    <>
                      <span style={{ backgroundColor: cfg.dot }} className="w-2 h-2 rounded-full flex-shrink-0" />
                      <span style={{ color: cfg.color }} className="font-semibold whitespace-nowrap">{cfg.label}</span>
                    </>
                  ) : <span className="text-[#919191]">—</span>;
                })()}
              </span>

              {/* Count segment */}
              <span className="w-[90px] px-3 py-1.5 bg-white flex items-center gap-1 border-l border-[#c8dcea] text-[#53545a]">
                {crosswalkLoading ? (
                  <div className="w-full h-2.5 bg-[#c8dcea] animate-pulse" />
                ) : (
                  <>
                    <span className="font-bold text-[#0077b5]">{proposal?.length ?? 0}</span>
                    <span>{(proposal?.length ?? 0) === 1 ? 'proposal' : 'proposals'}</span>
                  </>
                )}
              </span>
            </div>

            {allReviewed && (
              <button
                onClick={handleUndoAll}
                className="px-5 py-2 text-sm font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
              >
                Undo all
              </button>
            )}
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-sm text-[#919191] mb-4">Accept or reject answers suggested by questionnaire intelligence, or change the answer yourself. Click <span className="font-semibold text-[#53545a]">Show Explanation</span> to see why questionnaire intelligence chose that answer.</p>

        {/* Select All + Approve All / Reject All */}
        {anyPending && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#919191]/20">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="w-4 h-4 accent-[#F47F2E] cursor-pointer"
              />
              <span className="text-sm font-medium text-[#53545a]">
                Select All
              </span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleApproveAll}
                className="px-4 py-1.5 text-xs font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors"
              >
                Approve All
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-1.5 text-xs font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
              >
                Reject All
              </button>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="space-y-4">

          {questionsLoading ? (

            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="relative border-2  p-6">


                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="w-28 h-4 bg-[#e6eef5] animate-pulse mb-2" />
                      <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                    </div>
                    <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                  </div>

                  {/* Options skeleton */}
                  <div className="grid grid-cols-1 gap-3">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div key={j} className="flex items-center gap-4">
                        <div className="w-4 h-4-full bg-[#e6eef5] animate-pulse" />
                        <div className="w-40 h-3 bg-[#e6eef5] animate-pulse" />
                      </div>
                    ))}
                  </div>

                  {/* Confidence label skeleton */}
                  <div className="mt-4">
                    <div className="w-24 h-3 bg-[#e6eef5] animate-pulse mb-2" />
                    <div className="flex items-center gap-3">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <div key={k} className="w-14 h-3 bg-[#e6eef5] animate-pulse" />
                      ))}
                    </div>
                  </div>

                  {/* Undo button skeleton bottom-left */}
                  <div className="mt-4">
                    <div className="w-20 h-8 bg-[#e6eef5] animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginatedQuestions.length === 0 ? (
            <p className="text-center text-[#919191] py-8 text-sm">No questions found.</p>
          ) : paginatedQuestions.map((q) => (
            <QuestionCard key={q.id} question={q} />
          ))}
        </div>

        {/* Bottom: Approve Selected / Reject Selected + Pagination */}
        <div className="flex flex-col items-center gap-4 mt-6">
          {anySelected && (
            <div className="flex gap-3">
              <button
                onClick={handleApproveSelected}
                className="px-5 py-2 text-sm font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5] transition-colors"
              >
                Approve Selected
              </button>
              <button
                onClick={handleRejectSelected}
                className="px-5 py-2 text-sm font-semibold bg-[#F47F2E] text-white hover:bg-[#F47F2E] transition-colors"
              >
                Reject Selected
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center border border-[#919191] text-[#919191] hover:bg-[#919191]/10 disabled:opacity-40"
              >
                <FiChevronLeft className="text-sm" />
              </button>
              {(() => {
                const pages = [];
                const showEllipsisStart = currentPage > 3;
                const showEllipsisEnd = currentPage < totalPages - 2;

                // Always show first page
                pages.push(1);

                // Show ellipsis after first page if needed
                if (showEllipsisStart) {
                  pages.push('ellipsis-start');
                }

                // Show pages around current page
                for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }

                // Show ellipsis before last page if needed
                if (showEllipsisEnd) {
                  pages.push('ellipsis-end');
                }

                // Always show last page if more than 1 page
                if (totalPages > 1 && !pages.includes(totalPages)) {
                  pages.push(totalPages);
                }

                return pages.map((page, index) => {
                  if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                    return (
                      <span key={page} className="text-[#919191] text-sm px-1">
                        ···
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 flex items-center justify-center text-sm font-semibold transition-colors ${currentPage === page
                        ? 'bg-[#F47F2E] text-white'
                        : 'border border-[#919191] text-[#919191] hover:bg-[#919191]/10'
                        }`}
                    >
                      {page}
                    </button>
                  );
                });
              })()}
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center border border-[#919191] text-[#919191] hover:bg-[#919191]/10 disabled:opacity-40"
              >
                <FiChevronRight className="text-sm" />
              </button>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

// Wrapper component with context provider
const ReviewerQuestionnairesWithProvider = () => {
  return (
    <ReviewerProvider>
      <ReviewerQuestionnairesContent />
    </ReviewerProvider>
  );
};

export default ReviewerQuestionnairesWithProvider;


